import React, { useEffect, useRef, useMemo, useState } from 'react';
import { Segmented } from 'antd';
import Chart from 'chart.js/auto';
import dayjs from 'dayjs';
import { withLocale } from '../../l10n';
import { STATE_ACTIVE } from '../../utils/constants';
import './RenewalChart.scss';

// HORIZONTE DEL GRÁFICO

// Meses que se muestran desde el mes actual (incluido) hacia adelante.
const HORIZON_OPTIONS     = [6, 12];
const DEFAULT_HORIZON     = 6;
const HORIZON_STORAGE_KEY = 'renewalChartHorizon';

/**
 * Lee el último horizonte elegido. Si no hay o no es válido, devuelve el de por defecto.
 */
function readStoredHorizon() {
  try {
    const stored = Number(localStorage.getItem(HORIZON_STORAGE_KEY));
    return HORIZON_OPTIONS.includes(stored) ? stored : DEFAULT_HORIZON;
  } catch {
    return DEFAULT_HORIZON;
  }
}

function storeHorizon(value) {
  try {
    localStorage.setItem(HORIZON_STORAGE_KEY, String(value));
  } catch {
    // Sin almacenamiento disponible: la elección solo dura mientras la vista esté abierta
  }
}

// HELPER FUNCTIONS

/**
 * Genera los períodos mensuales: meses pasados con caducadas + mes actual + (monthsAhead - 1) futuros.
 */
function generateMonthlyPeriods(filteredData, monthsAhead = DEFAULT_HORIZON) {
  const today = dayjs();
  const periods = [];

  const expiredSubscriptions = filteredData.filter(s => s.renewal_diff < 0);
  let monthsBack = 0;

  if (expiredSubscriptions.length > 0) {
    const oldestDate = expiredSubscriptions
      .map(s => dayjs(s.date_to_renew, 'DD-MM-YYYY'))
      .sort((a, b) => a.valueOf() - b.valueOf())[0];

    monthsBack = Math.max(1, today.startOf('month').diff(oldestDate.startOf('month'), 'month'));
  }

  for (let i = monthsBack; i > 0; i--) {
    const startDate = today.clone().subtract(i, 'month').startOf('month');
    const endDate   = today.clone().subtract(i, 'month').endOf('month');
    periods.push({ startDate, endDate, label: startDate.format('MMM YYYY') });
  }

  periods.push({
    startDate: today.clone().startOf('month'),
    endDate:   today.clone().endOf('month'),
    label:     today.format('MMM YYYY'),
  });

  for (let i = 1; i < monthsAhead; i++) {
    const startDate = today.clone().add(i, 'month').startOf('month');
    const endDate   = today.clone().add(i, 'month').endOf('month');
    periods.push({ startDate, endDate, label: startDate.format('MMM YYYY') });
  }

  return periods;
}

/**
 * Agrupa las suscripciones por período y las clasifica como caducadas o por vencer.
 */
function groupSubscriptionsByPeriod(subscriptions, periods) {
  const expired  = new Array(periods.length).fill(0);
  const upcoming = new Array(periods.length).fill(0);

  subscriptions.forEach(subscription => {
    const renewalDate = dayjs(subscription.date_to_renew, 'DD-MM-YYYY');
    if (!renewalDate.isValid()) return;

    const periodIndex = periods.findIndex(period =>
      !renewalDate.isBefore(period.startDate) && !renewalDate.isAfter(period.endDate)
    );

    if (periodIndex !== -1) {
      if (subscription.renewal_diff < 0) {
        expired[periodIndex]++;
      } else {
        upcoming[periodIndex]++;
      }
    }
  });

  return { expired, upcoming };
}

/**
 * Devuelve la configuración de opciones para Chart.js.
 */
function getChartOptions(lang) {
  return {
    responsive: true,
    maintainAspectRatio: true,
    layout: {
      padding: { top: 10, bottom: 10 },
    },
    plugins: {
      legend: {
        position: 'top',
        labels: {
          font: { size: 13, weight: 600, family: "'Segoe UI', Roboto, 'Helvetica Neue', sans-serif" },
          padding: 16,
          usePointStyle: true,
          pointStyle: 'circle',
          color: '#262626',
        },
      },
      title: {
        display: true,
        text: lang.handle('Renovaciones'),
        font: { size: 16, weight: 600, family: "'Segoe UI', Roboto, 'Helvetica Neue', sans-serif" },
        color: '#262626',
        padding: { bottom: 20 },
      },
      tooltip: {
        backgroundColor: 'rgba(0, 0, 0, 0.75)',
        padding: 12,
        titleFont: { size: 13, weight: 600 },
        bodyFont:  { size: 12 },
        displayColors: true,
        borderColor: 'rgba(255, 255, 255, 0.1)',
        borderWidth: 1,
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          stepSize: 1,
          font: { size: 12, family: "'Segoe UI', Roboto, 'Helvetica Neue', sans-serif" },
          color: '#8c8c8c',
        },
        grid:   { color: 'rgba(0, 0, 0, 0.05)', drawBorder: false },
        border: { display: false },
      },
      x: {
        ticks: {
          font: { size: 12, family: "'Segoe UI', Roboto, 'Helvetica Neue', sans-serif" },
          color: '#8c8c8c',
        },
        grid:   { display: false, drawBorder: false },
        border: { display: false },
      },
    },
  };
}

// COMPONENTE PRINCIPAL 
const CANVAS_ID = 'renewalChart';

function RenewalChart({ data, types, tenantUuids, onPeriodSelect, lang, titleSuffix }) {
  const chartRef          = useRef(null);
  const periodsRef        = useRef([]);
  const isFirstDataRef    = useRef(true);
  const onPeriodSelectRef = useRef(onPeriodSelect);
  const [monthsAhead, setMonthsAhead] = useState(readStoredHorizon);

  const handleHorizonChange = (value) => {
    setMonthsAhead(value);
    storeHorizon(value);
  };

  // Mantener la ref del callback siempre actualizada sin recrear el chart
  useEffect(() => {
    onPeriodSelectRef.current = onPeriodSelect;
  }, [onPeriodSelect]);

  // Filtro defensivo: solo activas y solo tipos seleccionados
  const filteredData = useMemo(() =>
    (data || []).filter(item =>
      item.ki_subscription_state === STATE_ACTIVE &&
      (types || []).includes(item.subscription_type) &&
      (!tenantUuids || tenantUuids.includes(item.tenant_uuid))
    ),
    [data, types, tenantUuids]
  );

  // Crear el gráfico UNA SOLA VEZ al montar
  useEffect(() => {
    const ctx = document.getElementById(CANVAS_ID);
    if (!ctx) {
      console.error(`Canvas with ID ${CANVAS_ID} not found.`);
      return;
    }

    chartRef.current = new Chart(ctx, {
      type: 'bar',
      data: {
        labels: [],
        datasets: [
          {
            label: lang.handle('Caducadas'),
            data: [],
            backgroundColor: 'rgba(255, 77, 79, 0.8)',
            borderColor:     'rgba(255, 77, 79, 1)',
            borderWidth:     0,
            borderRadius:    4,
            borderSkipped:   false,
            hoverBackgroundColor: 'rgba(255, 77, 79, 1)',
            hoverBorderColor:     'rgba(255, 77, 79, 1)',
            hoverBorderWidth:     2,
          },
          {
            label: lang.handle('Por vencer'),
            data: [],
            backgroundColor: 'rgba(82, 196, 26, 0.8)',
            borderColor:     'rgba(82, 196, 26, 1)',
            borderWidth:     0,
            borderRadius:    4,
            borderSkipped:   false,
            hoverBackgroundColor: 'rgba(82, 196, 26, 1)',
            hoverBorderColor:     'rgba(82, 196, 26, 1)',
            hoverBorderWidth:     2,
          },
        ],
      },
      options: {
        ...getChartOptions(lang),
        onClick: (event, elements) => {
          if (elements.length > 0) {
            const index  = elements[0].index;
            const period = periodsRef.current[index];
            if (onPeriodSelectRef.current && period) {
              onPeriodSelectRef.current({
                from:  period.startDate.format('YYYY-MM-DD'),
                to:    period.endDate.format('YYYY-MM-DD'),
                label: period.label,
              });
            }
          }
        },
      },
    });

    return () => {
      chartRef.current?.destroy();
      chartRef.current = null;
    };
  }, [lang]);

  // Actualiza el título cuando cambia el filtro a una sola organización
  useEffect(() => {
    if (!chartRef.current) return;
    const base = lang.handle('Renovaciones');
    chartRef.current.options.plugins.title.text = titleSuffix
      ? `${base} - ${titleSuffix}`
      : base;
    chartRef.current.update('none');
  }, [titleSuffix, lang]);

  // Actualizar datos cuando filteredData cambia
  useEffect(() => {
    if (!chartRef.current) return;

    const periods = generateMonthlyPeriods(filteredData, monthsAhead);
    periodsRef.current = periods;

    const { expired, upcoming } = groupSubscriptionsByPeriod(filteredData, periods);

    chartRef.current.data.labels            = periods.map(p => p.label);
    chartRef.current.data.datasets[0].data  = expired;
    chartRef.current.data.datasets[1].data  = upcoming;

    if (isFirstDataRef.current && filteredData.length > 0) {
      chartRef.current.update();
      isFirstDataRef.current = false;
    } else {
      chartRef.current.update('none');
    }
  }, [filteredData, monthsAhead]);

  return (
    <div className="renewal-chart-container">
      <div className="renewal-chart-toolbar">
        <Segmented
          size="small"
          value={monthsAhead}
          onChange={handleHorizonChange}
          options={[
            { label: lang.handle('6 meses'), value: 6 },
            { label: lang.handle('1 año'),   value: 12 },
          ]}
        />
      </div>
      <canvas id={CANVAS_ID}></canvas>
    </div>
  );
}

const RenewalChartMemo = React.memo(RenewalChart);
export default withLocale(RenewalChartMemo);
